// Boorprofiel-tekening (SVG) — automatisch opgebouwd uit de lagen, de afwerking
//
// Een deel hiervan wordt hergebruikt door de mengmonster-tab, die dezelfde profielen naast
// elkaar zet op één NAP-as: `patternsSvg`, `soilFillMap`, `soilKind`, `admixKind` en
// `admixWidths` zijn daarom geëxporteerd. Eén tabel met patronen en klassebreedtes, drie
// tekeningen die hem gebruiken (deze, de mengmonster-tab en de veldapp).
// (vulling + achtergelaten objecten) en de peilbuizen van één boring.
//
// Volgt NEN 6693:2025, hoofdstuk 13 — de zeven eisen:
//   1. lagen op verticale schaal, vaste breedte per laag (textuurvakken)
//   2. textuurvak met de hoofdnaam-patroon (Figuur 1)
//   3. bijmengingen proportioneel rechts in het vak (~10% nauwkeurig, klasse-midden)
//   4. diepteschaal van maaiveld tot onderkant boring
//   5. opvulling van het boorgat (zwelklei + verloren casing expliciet)
//   6. geplaatste peilbuizen / installaties
//   7. geschatte grondwaterstand
// Plus tekstvak per laag (grondsoort uitgeschreven + waarnemingen) en legenda.
//
// Bewust SVG (niet canvas): scherp op scherm én in de latere A3-PDF-rapportage.

import {
  resolveTexture, isBlankTexture, resolveInstallTexture, resolveLeftBehindTexture, resolveTopCompletionTexture, TOP_COMPLETION_DEPTH_CM, bzbWarnings, texturePatternSvg, NEN_FIGURE_1, PEILBUIS_PARTS, nen6693,
} from '@mv/models';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Bijmengingsklasse → aandeel van het textuurvak. Klasse-MIDDEN, en bewust een weergavewaarde:
// NEN 6693 legt de klassen vast als gebieden in het driehoeksdiagram (Bijlage D), niet als één
// percentage per klasse, dus een tekening kan hier alleen een representatief midden pakken. Wat
// telt is dat de klassen onderling kloppen (sterk duidelijk breder dan matig) en dat 'zwak' niet
// zó smal wordt dat je hem niet meer ziet. Dezelfde tabel staat in de veldapp
// (mv-field-recorder/src/components/BoreholeProfile.tsx) — twee tekeningen, één tabel.
export const CLASS_FRACTION = { zwak: 0.10, matig: 0.20, sterk: 0.30, 'zeer sterk': 0.38, uiterst: 0.42 };

// Smalste bijmengingsbalk die nog leesbaar is (px), en het aandeel dat de bijmengingen samen
// hoogstens mogen innemen — de hoofdnaam moet het vak blijven dragen.
const MIN_ADMIX_PX = 10;
const MAX_ADMIX_SHARE = 0.6;

/** Breedte per bijmengingsbalk: proportioneel, maar niet onzichtbaar en niet overheersend. */
export function admixWidths(fracs, texW) {
  if (!fracs.length) return [];
  const cap = texW * MAX_ADMIX_SHARE;
  let w = fracs.map((f) => Math.max(f * texW, MIN_ADMIX_PX));
  const sum = w.reduce((s, x) => s + x, 0);
  if (sum > cap) { const k = cap / sum; w = w.map((x) => x * k); }
  return w;
}

/**
 * Grondsoort (op naam) → patroonsleutel uit Figuur 1.
 *
 * De norm kent acht symbolen, terwijl de codelijsten meer grondsoorten kennen. De
 * organische soorten delen er één: de tabel zegt letterlijk "veen (of detritus, humus,
 * gyttja)". Bruinkool hoort in diezelfde familie en gaat bij export ook naar veen.
 *
 * `isRock` komt van de aanroeper, want een gesteente is aan zijn naam niet te herkennen
 * zonder de codelijst: 'zandsteen' bevat 'zand' en werd zo als zándlaag getekend. Voor
 * gesteente kent Figuur 1 geen symbool, dus blijft het vak leeg -- liever niets beweren dan
 * een laag tonen die er niet ligt.
 *
 * @param {string} label   uitgeschreven grondsoort
 * @param {boolean} [isRock] label komt uit de gesteentelijst
 */
export function soilKind(label, isRock = false) {
  const s = (label || '').toLowerCase();
  if (isRock || s === 'rots') return 'bijzonder';
  if (s.includes('grind') || s.includes('keien')) return 'grind'; // keien: 5104, grover dan grind
  if (s.includes('zand')) return 'zand';
  if (s.includes('klei')) return 'klei';
  if (s.includes('silt') || s.includes('leem')) return 'silt';
  if (ORGANIC.some(o => s.includes(o))) return 'veen';
  if (s.includes('slib')) return 'slib';
  if (s.includes('water')) return 'water';
  return 'bijzonder';
}
/** Organische grondsoorten; delen één symbool (zie soilKind). */
const ORGANIC = ['veen', 'humus', 'humeus', 'detritus', 'gyttja', 'bruinkool', 'venig', 'organisch'];

export function admixKind(label) {
  const s = (label || '').toLowerCase();
  if (s.includes('grind') || s.includes('keiig')) return 'grind';
  if (s.includes('zand')) return 'zand';
  if (s.includes('silt') || s.includes('leem')) return 'silt';
  if (ORGANIC.some(o => s.includes(o)) || s.includes('hum')) return 'veen';
  if (s.includes('klei')) return 'klei';
  return 'silt';
}

// Kleuren en markeringen van NEN 6693, Figuur 1 (de tabel hoofdnaam/symbool/markering).
// De waarden zijn uit die tabel gemeten, niet benaderd: eerder stonden hier pastelvarianten
// waardoor grind lichtgeel was in plaats van oranje en slib een lichte stippel in plaats van
// zwart met een wit ruitpatroon. Een boorprofiel is een genormeerde tekening -- wie 'm leest
// herkent de laag aan kleur én markering, dus "ongeveer die kleur" is niet goed genoeg.
//
// De markering is overal ZWART, behalve bij slib: dat is wit op zwart.
const SOIL_COLORS = {
  zand:  '#fdd963', // geel, fijn gestippeld
  grind: '#f8823e', // oranje, grof gestippeld
  silt:  '#a6a6a6', // grijs, verticaal gestreept
  klei:  '#a9d18d', // groen, diagonaal linksonder → rechtsboven
  veen:  '#833c0a', // bruin, horizontaal gestreept
  slib:  '#000000', // zwart, dubbel gestreept haaks diagonaal (wit)
  water: '#0066ff', // blauw, golven
  bijzonder: '#ffffff', // leeg (wit), geen markering
};
const MARK = '#000000';

export const patternsSvg = (u) => `
  <pattern id="${u}_zand" width="6" height="6" patternUnits="userSpaceOnUse">
    <rect width="6" height="6" fill="${SOIL_COLORS.zand}"/><circle cx="1.5" cy="1.5" r="0.55" fill="${MARK}"/><circle cx="4.5" cy="4.5" r="0.55" fill="${MARK}"/></pattern>
  <pattern id="${u}_grind" width="9" height="9" patternUnits="userSpaceOnUse">
    <rect width="9" height="9" fill="${SOIL_COLORS.grind}"/><circle cx="2.5" cy="2.5" r="1.15" fill="${MARK}"/><circle cx="7" cy="7" r="1.15" fill="${MARK}"/></pattern>
  <pattern id="${u}_filtergrind" width="8" height="8" patternUnits="userSpaceOnUse">
    <rect width="8" height="8" fill="#eaf0f3"/><circle cx="2.5" cy="2.5" r="1.5" fill="#7f8c9b"/><circle cx="6" cy="6.5" r="1.3" fill="#7f8c9b"/></pattern>
  <pattern id="${u}_pbfilter" width="5" height="4" patternUnits="userSpaceOnUse">
    <rect width="5" height="4" fill="#b9bcc2"/><line x1="0" y1="1.5" x2="5" y2="1.5" stroke="#4f5966" stroke-width="0.9"/></pattern>
  <pattern id="${u}_klei" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
    <rect width="6" height="6" fill="${SOIL_COLORS.klei}"/><line x1="0" y1="0" x2="0" y2="6" stroke="${MARK}" stroke-width="0.9"/></pattern>
  <pattern id="${u}_silt" width="5" height="5" patternUnits="userSpaceOnUse">
    <rect width="5" height="5" fill="${SOIL_COLORS.silt}"/><line x1="1.5" y1="0" x2="1.5" y2="5" stroke="${MARK}" stroke-width="0.9"/></pattern>
  <pattern id="${u}_veen" width="6" height="5" patternUnits="userSpaceOnUse">
    <rect width="6" height="5" fill="${SOIL_COLORS.veen}"/><line x1="0" y1="1.5" x2="6" y2="1.5" stroke="${MARK}" stroke-width="0.9"/></pattern>
  <pattern id="${u}_slib" width="8" height="8" patternUnits="userSpaceOnUse">
    <rect width="8" height="8" fill="${SOIL_COLORS.slib}"/>
    <path d="M-2 2 L2 -2 M0 8 L8 0 M6 10 L10 6" stroke="#ffffff" stroke-width="0.9" fill="none"/>
    <path d="M-2 6 L2 10 M0 0 L8 8 M6 -2 L10 2" stroke="#ffffff" stroke-width="0.9" fill="none"/></pattern>
  <pattern id="${u}_water" width="10" height="5" patternUnits="userSpaceOnUse">
    <rect width="10" height="5" fill="${SOIL_COLORS.water}"/><path d="M0 2.5 q 2.5 -2 5 0 t 5 0" fill="none" stroke="${MARK}" stroke-width="0.8"/></pattern>
`;
// ─── Texturen per laag (instelbaar per bedrijf, zie @mv/models textures.ts) ─────────────
//
// De patronen hierboven blijven voor het boorgat (vulling, peilbuis) en als terugval; de lagen
// zelf tekenen met de textuur die het bedrijf voor die code heeft ingesteld, of de norm.

/** Smalle balk voor een BZB die de laag niet vult (zoals een bijmenging, maar vast van breedte). */
export const BZB_BAR_PX = 8;
/** De BZB-balken samen nemen hoogstens dit deel van het textuurvak in. */
const MAX_BZB_SHARE = 0.3;

// Een bijmenging tekent met de textuur van de bijbehorende grondsoort in dezelfde standaard,
// zodat een aangepast zand ook in 'zwak zandig' terugkomt. Silt heet in NEN 5104 leem ('L').
const ADMIX_SOIL_CODE = {
  nen6693: { zand: 'Z', grind: 'Gr', silt: 'S', klei: 'K', veen: 'V' },
  nen5104: { zand: 'Z', grind: 'G', silt: 'L', klei: 'K', veen: 'V' },
};
// Legenda 'Grondsoort': de acht symbolen van Figuur 1 met de code die ze in elke standaard hebben.
const LEGEND_SOIL_CODE = {
  nen6693: { zand: 'Z', veen: 'V', grind: 'Gr', slib: 'SLIB', klei: 'K', water: 'W', silt: 'S', bijzonder: 'BIJZ' },
  nen5104: { zand: 'Z', veen: 'V', grind: 'G', slib: 'S', klei: 'K', water: 'W', silt: 'L', bijzonder: null },
};

/** Onbekende standaard: NEN 6693 is leidend, net als bij de labels (zie boorprofiel-data). */
const drawStandard = (s) => (s === 'nen5104' ? 'nen5104' : 'nen6693');
const sameTexture = (a, b) => a.fill === b.fill && a.pattern === b.pattern && a.mark === b.mark;

/**
 * De texturen van één laag.
 *
 * Bestaat de laag uit een BZB (gradatie dominant/overvloedig/volledig/uiterst), dan vult die
 * BZB het hele vak. Anders de grondsoort, met de bijmengingen en de overige BZB's als balken.
 * `custom` = wijkt af van de norm (dan zegt de voetregel dat erbij).
 *
 * @param {{soilCode?:string|null, soilLabel?:string, isRock?:boolean, admix?:Array<{label:string,grad:string}>,
 *          bzb?:Array<{code:string|null,label:string,fillsLayer:boolean}>}} l
 * @param {string|null} standard
 * @param {object|null} overrides  afwijkingen van het bedrijf
 */
export function layerTextures(l, standard, overrides) {
  const std = drawStandard(standard);
  const pick = (category, code, label) => {
    const t = resolveTexture(std, category, code, overrides, label);
    return { t, custom: !!overrides && !sameTexture(t, resolveTexture(std, category, code, null, label)) };
  };
  const bzb = l.bzb || [];
  const filler = bzb.find((b) => b.fillsLayer);
  let main;
  if (filler) main = { ...pick('bzb', filler.code, filler.label), label: filler.label };
  // Een gesteente zonder bruikbare code: Figuur 1 kent geen symbool, dus leeg (zie soilKind).
  else if (l.isRock && !l.soilCode) main = { t: NEN_FIGURE_1.bijzonder, custom: false };
  else main = pick('soil', l.soilCode || null, l.soilLabel || '');
  const admix = (l.admix || []).map((a) => ({
    ...pick('soil', ADMIX_SOIL_CODE[std][admixKind(a.label)], a.label),
    frac: CLASS_FRACTION[(a.grad || '').toLowerCase()] ?? 0.1,
  })).filter((a) => a.frac > 0);
  // Een BZB als bijmenging tekent alleen als het bedrijf er een kleur of patroon aan gaf: de
  // standaard is wit zonder patroon (BZB_DEFAULT), en een wit streepje zegt niets. Een laag die
  // uit een BZB bestaat (filler hierboven) blijft altijd zichtbaar, desnoods als witte laag.
  const bars = bzb.filter((b) => !b.fillsLayer).map((b) => ({ ...pick('bzb', b.code, b.label), label: b.label }))
    .filter((b) => !isBlankTexture(b.t));
  return { main, admix, bzb: bars, filler: filler ? { ...main } : null };
}

/**
 * Breedtes in het textuurvak: hoofdnaam links, dan de bijmengingen, dan de BZB-balken.
 * Zonder BZB's precies hetzelfde als admixWidths alleen.
 */
export function layerWidths(lt, texW) {
  const bzbTotal = Math.min(lt.bzb.length * BZB_BAR_PX, texW * MAX_BZB_SHARE);
  const bzbW = lt.bzb.map(() => bzbTotal / lt.bzb.length);
  const admixW = admixWidths(lt.admix.map((a) => a.frac), texW - bzbTotal);
  const mainW = texW - bzbTotal - admixW.reduce((s, w) => s + w, 0);
  return { mainW, admixW, bzbW };
}

/** De acht Figuur 1-symbolen met de texturen die nu gelden. */
export function soilLegend(standard, overrides) {
  const std = drawStandard(standard);
  return SOIL_LEGEND.map(([family, label]) => {
    const code = LEGEND_SOIL_CODE[std][family];
    return [code ? resolveTexture(std, 'soil', code, overrides) : NEN_FIGURE_1[family], label];
  });
}

/**
 * Patroon-definities voor de texturen die een tekening gebruikt. `ref(t)` geeft de fill, en
 * `defs()` de <pattern>-elementen voor alles wat er tot dan toe om gevraagd is. Een textuur
 * zonder patroon is gewoon een kleur.
 */
export function createTextureDefs(prefix) {
  const ids = new Map();
  const defs = [];
  return {
    ref(t) {
      if (t.pattern === 'none') return t.fill;
      const k = `${t.fill}|${t.pattern}|${t.mark}`;
      if (!ids.has(k)) {
        const id = `${prefix}_t${ids.size}`;
        ids.set(k, id);
        defs.push(texturePatternSvg(id, t));
      }
      return `url(#${ids.get(k)})`;
    },
    defs: () => defs.join(''),
  };
}

export const soilFillMap = (u) => ({ zand: `url(#${u}_zand)`, grind: `url(#${u}_grind)`, klei: `url(#${u}_klei)`, silt: `url(#${u}_silt)`, veen: `url(#${u}_veen)`, slib: `url(#${u}_slib)`, water: `url(#${u}_water)`, bijzonder: SOIL_COLORS.bijzonder });
const SOIL_LEGEND = [
  ['zand', 'zand'], ['veen', 'veen'], ['grind', 'grind'], ['slib', 'slib'],
  ['klei', 'klei'], ['water', 'water'], ['silt', 'silt'], ['bijzonder', 'bijzondere laag'],
];

const LEGEND_TITLES = { soil: 'Grondsoort', gat: 'Boorgat en installatie', bzb: 'Bijzondere bestanddelen' };

/** Wat er in het boorgat getekend wordt: vulling, peilbuizen, achtergelaten objecten, bovenafwerking. */
function installation(d) {
  const overrides = d.textures || null;
  return {
    fillings: (d.fillings || []).filter(f => f.van != null && f.tot != null && f.tot > f.van),
    peilbuizen: d.peilbuizen || [],
    achtergelaten: (d.achtergelaten || []).filter(o => o.van != null && o.tot != null && o.tot > o.van),
    // Bovenafwerking: alleen met een eigen kleur of patroon (standaard leeg, dan niet getekend).
    topT: d.bovenafwerking?.type ? resolveTopCompletionTexture(d.bovenafwerking.type, overrides) : null,
  };
}

/**
 * De legenda van één boring als `[fill, label]`-paren per sectie (soil, gat, bzb). Boorgat en
 * bijzondere bestanddelen noemen alleen wat er in deze boring zit. `fill` is een kleur, een
 * patroon-url uit `tex`, of 'gws' (grondwaterstand, getekend als lijn).
 */
function legendSections(d, tex, lts) {
  const overrides = d.textures || null;
  const fillT = (code) => resolveInstallTexture('afwerking', code, overrides);
  const pbT = (part) => resolveInstallTexture('peilbuis', part, overrides);
  const { fillings, peilbuizen, achtergelaten, topT } = installation(d);
  const gat = [];
  const seenFill = new Set();
  for (const f of fillings) {
    if (seenFill.has(f.code)) continue;
    seenFill.add(f.code);
    gat.push([tex.ref(fillT(f.code)), (nen6693.vulmateriaalLabel(f.code) || 'vulling').toLowerCase()]);
  }
  if (peilbuizen.some(pb => pb.filterTop != null)) gat.push([tex.ref(pbT(PEILBUIS_PARTS.blind)), 'peilbuis, blind']);
  if (peilbuizen.some(pb => pb.filterTop != null && pb.filterBottom != null)) gat.push([tex.ref(pbT(PEILBUIS_PARTS.filter)), 'peilbuis, filter']);
  // Achtergelaten in de grond: per type een eigen textuur (tab Afwerking).
  const seenLeft = new Set();
  for (const o of achtergelaten) {
    if (seenLeft.has(o.type)) continue;
    seenLeft.add(o.type);
    gat.push([tex.ref(resolveLeftBehindTexture(o.type, overrides)), (o.typeLabel || 'achtergelaten object').toLowerCase()]);
  }
  if (topT) gat.push([tex.ref(topT), (d.bovenafwerking.label || 'bovenafwerking').toLowerCase()]);
  if (d.gwsCmMv != null && d.gwsCmMv >= 0) gat.push(['gws', 'grondwaterstand']);

  const bzb = new Map();
  for (const lt of lts) {
    if (lt.filler) bzb.set(lt.filler.label, lt.filler.t);
    for (const b of lt.bzb) bzb.set(b.label, b.t);
  }
  return {
    soil: soilLegend(d.standard, overrides).map(([t, l]) => [tex.ref(t), l]),
    gat,
    bzb: [...bzb].map(([l, t]) => [tex.ref(t), l]),
  };
}

/**
 * @param {object} d
 * @param {{name,onderzoek,projectCode,date,maaiveld,referencePlane,boormeester}} d.header
 * @param {Array<{from:number,to:number,soilLabel:string,soilCode?:string,isRock?:boolean,admix:Array<{label:string,grad:string}>,
 *                bzb?:Array<{code:string|null,label:string,fillsLayer:boolean}>,line2:string}>} d.layers
 * @param {string|null} [d.standard]  nen5104 | nen6693 — bepaalt hoe de codes gelezen worden
 * @param {object|null} [d.textures]  textuur-afwijkingen van het bedrijf (zie layerTextures)
 * @param {Array<{van:number,tot:number,code:string}>} d.fillings
 * @param {Array<{van:number,tot:number,aard:string,type?:string|null,typeLabel:string}>} d.achtergelaten
 * @param {{type:string|null,label?:string}|null} [d.bovenafwerking]  getekend alleen met een eigen textuur
 * @param {Array<{filterTop:number|null,filterBottom:number|null}>} d.peilbuizen
 * @param {number|null} d.gwsCmMv
 * @param {number} d.boordiepteCm
 * @param {number} [d.profileHeightPx]  hoogte van de lagenkolom; standaard ~470 px (begrensd)
 * @param {number} [d.pxPerCm]  vaste verticale schaal (de PDF-export kiest zo een 1:N, zie boorprofiel-scale.js)
 * @param {number} [d.tickStepCm]  afstand tussen de dieptestreepjes; standaard 50 cm (100 cm voorbij 8 m)
 * @param {boolean} [d.hideLegend]  legenda rechts weglaten (de tekening wordt smaller)
 * @returns {string} volledige <svg>…</svg>
 */
let __bpSeq = 0;
export function buildBoorprofielSvg(d) {
  const u = 'bp' + (++__bpSeq); // unieke namespace voor de patroon-id's van dit profiel
  const layers = (d.layers || []).filter(l => l.to > l.from);
  // Texturen: per laag opgezocht (code + standaard + afwijkingen van het bedrijf).
  const overrides = d.textures || null;
  const tex = createTextureDefs(u);
  const lts = layers.map(l => layerTextures(l, d.standard, overrides));
  const legend = legendSections(d, tex, lts);
  // Diepteschaal moet ALLES dekken wat getekend wordt (lagen, vulling, achtergelaten
  // objecten én peilbuizen) — anders steekt een diepere peilbuis buiten de tekening uit.
  const numOr0 = (v) => Number(v) || 0;
  // Werkelijke einddiepte van de boring (voor de kop) …
  const einddiepteCm = Math.max(d.boordiepteCm || 0, ...layers.map(l => l.to), 0);
  // … en de schaal-diepte die álles dekt wat getekend wordt (ook een diepere peilbuis).
  const depthCm = Math.max(
    einddiepteCm,
    ...(d.fillings || []).map(f => numOr0(f.tot)),
    ...(d.achtergelaten || []).map(o => numOr0(o.tot)),
    ...(d.peilbuizen || []).map(pb => Math.max(numOr0(pb.filterTop), numOr0(pb.filterBottom))),
    1,
  );

  // Boorgat en installatie: vulmaterialen en peilbuis in de textuur van het bedrijf (tabs
  // Afwerking en Peilbuis op /settings/textures).
  const fillT = (code) => resolveInstallTexture('afwerking', code, overrides);
  const pbT = (part) => resolveInstallTexture('peilbuis', part, overrides);
  const { fillings, peilbuizen, achtergelaten, topT } = installation(d);
  const gwsColor = pbT(PEILBUIS_PARTS.gws).fill;
  const soilLeg = legend.soil, gatLeg = legend.gat, bzbLeg = legend.bzb;

  // Layout — legenda staat rechts (verticale secties onder elkaar), naast het profiel.
  const padX = 14, colHeadH = 18;
  const axisX = padX, axisW = 34;
  const gatX = axisX + axisW + 4, gatW = 40;
  const texX = gatX + gatW + 10, texW = 120;
  const descX = texX + texW + 12, descW = 195;
  const legendX = descX + descW + 20, legendW = 170;
  const width = d.hideLegend ? descX + descW + padX : legendX + legendW + padX;
  // Kopregel met maaiveld, referentievlak, einddiepte en boormeester. Zonder legenda is de
  // tekening smal; dan over meer regels verdelen in plaats van over de rand te lopen (≈0,52 × fontgrootte per teken).
  const h = d.header || {};
  const metaItems = [
    h.maaiveld != null ? `Maaiveld ${h.maaiveld}` : '',
    h.referencePlane ? `Referentievlak ${h.referencePlane}` : '',
    `Einddiepte ${(einddiepteCm / 100).toFixed(2)} m -mv`,
    h.boormeester ? `Boormeester ${h.boormeester}` : '',
  ].filter(Boolean);
  const metaMaxChars = Math.floor((width - 2 * padX) / (11 * 0.52));
  const metaLines = [];
  for (const item of metaItems) {
    const last = metaLines.length - 1;
    const cand = last >= 0 ? `${metaLines[last]}      ${item}` : item;
    if (last >= 0 && cand.length <= metaMaxChars) metaLines[last] = cand; else metaLines.push(item);
  }
  const headerH = 56 + (metaLines.length - 1) * 14;
  // Standaard ~470 px hoog, begrensd zodat een heel ondiepe of diepe boring leesbaar blijft. Een
  // aanroeper met meer ruimte (het textuurvoorbeeld) kan een vaste hoogte vragen.
  const pxPerCm = d.pxPerCm || (d.profileHeightPx ? d.profileHeightPx / depthCm : Math.max(0.55, Math.min(2.4, 470 / depthCm)));
  const top = headerH + colHeadH;
  const profH = depthCm * pxPerCm;
  const profBottom = top + profH;
  const y = (cm) => top + cm * pxPerCm;

  // Legenda-hoogte: twee secties (grondsoort + boorgat) onder elkaar.
  const legRowH = 15, legHeadH = 16, legSecGap = 14, legendTop = top + 8;
  const legSecH = (n) => legHeadH + n * legRowH;
  const legendH = legSecH(SOIL_LEGEND.length) + (gatLeg.length ? legSecGap + legSecH(gatLeg.length) : 0)
    + (bzbLeg.length ? legSecGap + legSecH(bzbLeg.length) : 0);
  // De onderkant (en dus de hoogte van de tekening) hangt ook af van de laagbeschrijvingen, die
  // bij veel dunne lagen onder het profiel uit kunnen schuiven. Die kennen we pas na de lagen,
  // dus de <svg>-kop en de achtergrond worden aan het eind ingevuld.
  const parts = [];
  const openAt = parts.push('') - 1;
  const defsAt = parts.push('') - 1; // wordt aan het eind gevuld, als alle texturen bekend zijn
  const backgroundAt = parts.push('') - 1;

  // ── Header ────────────────────────────────────────────────────────────────
  parts.push(`<text x="${padX}" y="20" font-size="14" font-weight="700" fill="#1a1a1a">Boring ${esc(h.name || '')}</text>`);
  if (h.onderzoek || h.projectCode) parts.push(`<text x="${padX + 90}" y="20" font-size="11" fill="#555">${esc([h.onderzoek, h.projectCode ? `project ${h.projectCode}` : ''].filter(Boolean).join(' · '))}</text>`);
  if (h.date) parts.push(`<text x="${width - padX}" y="20" font-size="11" fill="#555" text-anchor="end">${esc(h.date)}</text>`);
  metaLines.forEach((line, i) => parts.push(`<text x="${padX}" y="${42 + i * 14}" font-size="11" fill="#333">${esc(line)}</text>`));
  parts.push(`<line x1="0" y1="${headerH - 4}" x2="${width}" y2="${headerH - 4}" stroke="#e5e5e5"/>`);

  // ── Kolomkoppen ───────────────────────────────────────────────────────────
  const colHead = (x, t, anchor = 'start') => `<text x="${x}" y="${headerH + 12}" font-size="9.5" font-weight="700" letter-spacing="0.03em" fill="#888" text-anchor="${anchor}">${esc(t)}</text>`;
  parts.push(colHead(axisX, 'm -mv'));
  parts.push(colHead(gatX, 'boorgat'));
  parts.push(colHead(texX, 'textuur'));
  parts.push(colHead(descX, 'laagbeschrijving'));

  // ── Diepteschaal (elke 0,5 m een lijn + label) ────────────────────────────
  const stepCm = d.tickStepCm || (depthCm > 800 ? 100 : 50);
  for (let cm = 0; cm <= depthCm + 0.1; cm += stepCm) {
    const yy = y(cm);
    parts.push(`<line x1="${gatX - 4}" y1="${yy}" x2="${texX + texW}" y2="${yy}" stroke="#ededed" stroke-width="1"/>`);
    parts.push(`<text x="${axisX + axisW}" y="${yy + 3}" font-size="9" fill="#666" text-anchor="end">${(cm / 100).toFixed(2)}</text>`);
  }

  // ── Boorgat: vulling + achtergelaten objecten + peilbuis ─────────────────
  // Vulling over de volle boorgatbreedte.
  for (const f of fillings) {
    parts.push(`<rect x="${gatX}" y="${y(f.van)}" width="${gatW}" height="${(f.tot - f.van) * pxPerCm}" fill="${tex.ref(fillT(f.code))}" stroke="#cfcfcf" stroke-width="0.5"/>`);
  }
  parts.push(`<rect x="${gatX}" y="${top}" width="${gatW}" height="${profH}" fill="none" stroke="#c8c8c8" stroke-width="1"/>`);
  // Peilbuis (blind 0..filterTop, filter filterTop..filterBottom) — smalle balk rechts in het gat.
  const pbW = 9;
  for (const pb of peilbuizen) {
    const fTop = pb.filterTop != null ? Number(pb.filterTop) : null;
    const fBot = pb.filterBottom != null ? Number(pb.filterBottom) : null;
    const bx = gatX + gatW - pbW - 2;
    if (fTop != null) parts.push(`<rect x="${bx}" y="${y(0)}" width="${pbW}" height="${Math.max(0, fTop) * pxPerCm}" fill="${tex.ref(pbT(PEILBUIS_PARTS.blind))}" stroke="#7d8288" stroke-width="0.5"/>`);
    if (fTop != null && fBot != null && fBot > fTop) parts.push(`<rect x="${bx}" y="${y(fTop)}" width="${pbW}" height="${(fBot - fTop) * pxPerCm}" fill="${tex.ref(pbT(PEILBUIS_PARTS.filter))}" stroke="#7d8288" stroke-width="0.5"/>`);
  }
  // Achtergelaten objecten — oranje/roodbruine balk links in het gat.
  for (const o of achtergelaten) {
    parts.push(`<rect x="${gatX + 1}" y="${y(o.van)}" width="6" height="${(o.tot - o.van) * pxPerCm}" fill="${tex.ref(resolveLeftBehindTexture(o.type, overrides))}" stroke="#b06a2a" stroke-width="0.5"><title>${esc(o.typeLabel || 'achtergelaten object')}</title></rect>`);
  }
  // Bovenafwerking: band over de hele breedte van het gat, over vulling en peilbuis heen.
  if (topT) {
    const h = Math.min(TOP_COMPLETION_DEPTH_CM, depthCm) * pxPerCm;
    parts.push(`<rect x="${gatX}" y="${y(0)}" width="${gatW}" height="${h}" fill="${tex.ref(topT)}" stroke="#7d8288" stroke-width="0.6"><title>${esc(d.bovenafwerking.label || 'bovenafwerking')}</title></rect>`);
  }

  // ── Textuurvakken + proportionele bijmengingen + laagbeschrijving ────────
  // Woord-afbreken binnen de beschrijvingskolom (SVG kent geen auto-wrap) zodat de tekst
  // niet in de legenda loopt. Breedte geschat op tekengrootte (≈0.52·fontgrootte per teken).
  const wrapText = (text, maxW, fontSize) => {
    const maxChars = Math.max(6, Math.floor(maxW / (fontSize * 0.52)));
    const words = String(text || '').split(/\s+/).filter(Boolean);
    const lines = [];
    let cur = '';
    for (const w of words) {
      const cand = cur ? `${cur} ${w}` : w;
      if (cand.length > maxChars && cur) { lines.push(cur); cur = w; } else cur = cand;
    }
    if (cur) lines.push(cur);
    return lines.length ? lines : [''];
  };
  const tspans = (lines, x, dy) => lines.map((ln, i) => `<tspan x="${x}" ${i ? `dy="${dy}"` : ''}>${esc(ln)}</tspan>`).join('');

  let prevTextBottom = top - 6; // onthoud waar het vorige tekstblok eindigde (de-collisie)
  for (const [li, l] of layers.entries()) {
    const yy = y(l.from), hh = (l.to - l.from) * pxPerCm;
    // Bijmengingen: fracties uit klasse-midden, som mag de hoofdnaam niet verdringen. Daarna
    // de BZB's die de laag niet vullen, als smalle balken met een witte scheiding.
    const lt = lts[li];
    const { mainW, admixW, bzbW } = layerWidths(lt, texW);
    parts.push(`<rect x="${texX}" y="${yy}" width="${mainW}" height="${hh}" fill="${tex.ref(lt.main.t)}" stroke="#c8c8c8" stroke-width="0.5"/>`);
    let ax = texX + mainW;
    lt.admix.forEach((a, i) => {
      const w = admixW[i];
      parts.push(`<rect x="${ax}" y="${yy}" width="${w}" height="${hh}" fill="${tex.ref(a.t)}" stroke="#c8c8c8" stroke-width="0.5"/>`);
      ax += w;
    });
    lt.bzb.forEach((b, i) => {
      const w = bzbW[i];
      parts.push(`<rect x="${ax}" y="${yy}" width="${w}" height="${hh}" fill="${tex.ref(b.t)}"><title>${esc(b.label)}</title></rect>`);
      parts.push(`<line x1="${ax}" y1="${yy}" x2="${ax}" y2="${yy + hh}" stroke="#ffffff" stroke-width="1.5"/>`);
      ax += w;
    });
    parts.push(`<rect x="${texX}" y="${yy}" width="${texW}" height="${hh}" fill="none" stroke="#b8b8b8" stroke-width="0.8"/>`);
    // Laagbeschrijving (grondsoort uitgeschreven + waarnemingen). Schuif het blok omlaag
    // als het anders op het vorige zou botsen (dunne, opeenvolgende lagen).
    // Waarschuwing: een BZB in deze laag waar het bedrijf 'Waarschuwing' bij aanzette (tab BZBs).
    // Een driehoek rechts in de beschrijvingskolom; de tekst houdt daar ruimte voor vrij.
    const warns = bzbWarnings(drawStandard(d.standard), l.bzb || [], overrides);
    const textW = warns.length ? descW - 26 : descW;
    // Zonder grondsoort de BZB waar de laag uit bestaat (asfalt, puin).
    const main = l.soilLabel || (l.bzb || []).filter((b) => b.fillsLayer).map((b) => b.label).join(' en ');
    const line1 = [main, ...(l.admix || []).map(a => [a.grad, a.label].filter(Boolean).join(' '))].filter(Boolean).join(', ');
    const l1 = wrapText(line1, textW, 11);
    const l2 = l.line2 ? wrapText(l.line2, textW, 10) : [];
    const ty = Math.max(yy + 12, prevTextBottom + 13);
    // Dun verbindingslijntje als het label duidelijk onder de laag-top is geschoven.
    if (ty > yy + 18) parts.push(`<line x1="${descX - 8}" y1="${yy + 3}" x2="${descX - 8}" y2="${ty - 4}" stroke="#e2e2e2" stroke-width="1"/>`);
    parts.push(`<text x="${descX}" y="${ty}" font-size="11" font-weight="600" fill="#1a1a1a">${tspans(l1, descX, 13)}</text>`);
    if (warns.length) {
      // In het midden van de laag (binnen de laag gehouden), rechts in de beschrijvingskolom.
      // Uitroepteken als vorm, niet als tekst: een letter van een paar pixels wordt wazig.
      const S = 18;
      const wx = descX + descW - S;
      const wy = Math.max(yy + 1, Math.min(yy + hh - S - 1, yy + hh / 2 - S / 2));
      const cx = wx + S / 2;
      parts.push(`<g class="bzb-warning"><title>${esc(`Let op: ${warns.join(', ')}`)}</title>`
        + `<path d="M${cx} ${wy + 1.2} L${wx + S - 0.8} ${wy + S - 1.5} Q${wx + S - 0.3} ${wy + S - 0.4} ${wx + S - 1.6} ${wy + S - 0.4} L${wx + 1.6} ${wy + S - 0.4} Q${wx + 0.3} ${wy + S - 0.4} ${wx + 0.8} ${wy + S - 1.5} Z" fill="#f5b400" stroke="#a16c00" stroke-width="1" stroke-linejoin="round"/>`
        + `<rect x="${cx - 1.1}" y="${wy + 6}" width="2.2" height="6.2" rx="1.1" fill="#1f1f1f"/>`
        + `<circle cx="${cx}" cy="${wy + 14.4}" r="1.25" fill="#1f1f1f"/></g>`);
    }
    let cursor = ty + (l1.length - 1) * 13;
    if (l2.length) {
      const l2y = cursor + 12;
      parts.push(`<text x="${descX}" y="${l2y}" font-size="10" fill="#666">${tspans(l2, descX, 12)}</text>`);
      cursor = l2y + (l2.length - 1) * 12;
    }
    prevTextBottom = cursor + 6;
  }

  const contentBottom = Math.max(profBottom, prevTextBottom, d.hideLegend ? 0 : legendTop + legendH);
  // Geen voetregel: de tekening is af na het profiel, de beschrijvingen en de legenda.
  const totalH = contentBottom + 12;
  parts[openAt] = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${totalH}" width="100%" style="max-width:${width}px; font-family:system-ui,-apple-system,sans-serif;">`;
  parts[backgroundAt] = `<rect x="0" y="0" width="${width}" height="${totalH}" fill="#ffffff"/>`;

  // ── Grondwaterstand ───────────────────────────────────────────────────────
  if (d.gwsCmMv != null && d.gwsCmMv >= 0 && d.gwsCmMv <= depthCm) {
    const gy = y(d.gwsCmMv);
    parts.push(`<line x1="${gatX - 6}" y1="${gy}" x2="${texX + texW}" y2="${gy}" stroke="${gwsColor}" stroke-width="1.4"/>`);
    parts.push(`<path d="M${gatX - 6} ${gy} l 5 -3 l 0 6 z" fill="${gwsColor}"/>`);
    parts.push(`<text x="${gatX - 8}" y="${gy - 3}" font-size="9" fill="${gwsColor}" text-anchor="end">GWS ${(d.gwsCmMv / 100).toFixed(2)}</text>`);
  }

  // ── Legenda (rechts, verticale secties onder elkaar) ─────────────────────
  if (!d.hideLegend) {
    parts.push(`<line x1="${legendX - 12}" y1="${legendTop - 4}" x2="${legendX - 12}" y2="${contentBottom}" stroke="#ededed"/>`);
    const drawLegSection = (x, startY, title, items) => {
      parts.push(`<text x="${x}" y="${startY}" font-size="10" font-weight="700" fill="#555">${esc(title)}</text>`);
      let yy = startY + legHeadH;
      for (const [fill, label] of items) {
        if (fill === 'gws') parts.push(`<line x1="${x}" y1="${yy - 4}" x2="${x + 16}" y2="${yy - 4}" stroke="${gwsColor}" stroke-width="1.6"/>`);
        else parts.push(`<rect x="${x}" y="${yy - 8}" width="16" height="11" fill="${fill}" stroke="#bbb" stroke-width="0.5"/>`);
        parts.push(`<text x="${x + 22}" y="${yy}" font-size="9.5" fill="#444">${esc(label)}</text>`);
        yy += legRowH;
      }
      return yy;
    };
    let legY = drawLegSection(legendX, legendTop + 4, LEGEND_TITLES.soil, soilLeg);
    if (gatLeg.length) legY = drawLegSection(legendX, legY + legSecGap, LEGEND_TITLES.gat, gatLeg);
    if (bzbLeg.length) drawLegSection(legendX, legY + legSecGap, LEGEND_TITLES.bzb, bzbLeg);
  }

  parts[defsAt] = `<defs>${patternsSvg(u)}${tex.defs()}</defs>`;
  parts.push(`</svg>`);
  return parts.join('');
}

/**
 * Eén legenda voor meerdere boorprofielen (een pagina van de export): alles wat op die profielen
 * voorkomt, samengevoegd, in rijen naast elkaar. Zo kunnen de profielen zelf zonder eigen legenda
 * (hideLegend) en passen er twee naast elkaar op een pagina zonder dat de tekst onleesbaar klein
 * wordt.
 *
 * @param {object[]} profiles  `d`-objecten zoals voor buildBoorprofielSvg
 * @param {{width?:number}} [opts]  breedte in px (de verhouding px : mm bepaalt de aanroeper)
 * @returns {{svg:string, w:number, h:number}}
 */
export function buildGedeeldeLegendaSvg(profiles, { width = 900 } = {}) {
  const u = 'bpl' + (++__bpSeq);
  const tex = createTextureDefs(u);
  const secties = { soil: new Map(), gat: new Map(), bzb: new Map() };
  for (const d of profiles || []) {
    const layers = (d.layers || []).filter(l => l.to > l.from);
    const lts = layers.map(l => layerTextures(l, d.standard, d.textures || null));
    const leg = legendSections(d, tex, lts);
    for (const k of Object.keys(secties)) for (const [fill, label] of leg[k]) if (!secties[k].has(label)) secties[k].set(label, fill);
  }
  const gwsColor = resolveInstallTexture('peilbuis', PEILBUIS_PARTS.gws, profiles?.[0]?.textures || null).fill;
  const pad = 4, itemW = 150, rowH = 15, headH = 15, secGap = 8;
  const perRij = Math.max(1, Math.floor((width - 2 * pad) / itemW));
  const parts = [];
  let y = pad;
  for (const k of ['soil', 'gat', 'bzb']) {
    const items = [...secties[k]].map(([label, fill]) => [fill, label]);
    if (!items.length) continue;
    if (y > pad) y += secGap;
    parts.push(`<text x="${pad}" y="${y + 10}" font-size="10" font-weight="700" fill="#555">${esc(LEGEND_TITLES[k])}</text>`);
    y += headH;
    items.forEach(([fill, label], i) => {
      const x = pad + (i % perRij) * itemW;
      const yy = y + Math.floor(i / perRij) * rowH + 10;
      if (fill === 'gws') parts.push(`<line x1="${x}" y1="${yy - 4}" x2="${x + 16}" y2="${yy - 4}" stroke="${gwsColor}" stroke-width="1.6"/>`);
      else parts.push(`<rect x="${x}" y="${yy - 8}" width="16" height="11" fill="${fill}" stroke="#bbb" stroke-width="0.5"/>`);
      parts.push(`<text x="${x + 22}" y="${yy}" font-size="9.5" fill="#444">${esc(label)}</text>`);
    });
    y += Math.ceil(items.length / perRij) * rowH;
  }
  const h = y + pad;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${h}" width="100%" style="font-family:system-ui,-apple-system,sans-serif;">`
    + `<defs>${patternsSvg(u)}${tex.defs()}</defs>`
    + `<line x1="0" y1="0.5" x2="${width}" y2="0.5" stroke="#e3e3e3"/>`
    + parts.join('') + '</svg>';
  return { svg, w: width, h };
}
